import React, { FC } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { ChargeFormEntry } from '../../types';
import { AmountField } from '../ChargesTableRow/components/Fields/AmountField';
import { ContainedButton } from 'components/ContainedButton';
import { extractData } from 'views/orders/new-charges/charges-e/components/document-fees-modal/services';
import { useMobileCardBaseStyles } from './styles';

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(1.5),
  },
  documentLabel: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  descriptionText: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 500,
    color: theme.palette.primary.dark,
    lineHeight: 1.4,
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
}));

interface Props {
  charge: ChargeFormEntry;
  index: number;
  currentLetter?: string;
  textPredectiveCharges: any[];
  onDelete: (rowIndex: number) => void;
  onEdit: (charge: ChargeFormEntry, rowIndex: number) => void;
  onEditSplitAmount: (charge: ChargeFormEntry, kind: 'Payee' | 'Payer' | 'Tax') => void;
  isPartiesOpen: boolean;
  onToggleParties: () => void;
  onChargeAmountChange: (newAmount: number | null) => void;
  onChargePaidByAmountChange: () => void;
}

export const MobileChargeDocumentCard: FC<Props> = ({
  charge,
  index,
  currentLetter,
  textPredectiveCharges,
  onDelete,
  onEdit,
  onEditSplitAmount,
  isPartiesOpen,
  onToggleParties,
  onChargeAmountChange,
  onChargePaidByAmountChange,
}) => {
  const localClasses = useStyles();
  const baseClasses = useMobileCardBaseStyles();
  const classes = { ...localClasses, ...baseClasses };
  const { t } = useTranslation();

  const isCharges1200 = currentLetter === '1200';
  const documentDescription = extractData(charge.description)?.document ?? charge.description ?? '';

  return (
    <Paper className={classes.card} variant="outlined">
      {/* Header */}
      <Box className={classes.header}>
        <Typography className={classes.documentLabel}>{t('charges:document')}</Typography>
        <Typography className={classes.descriptionText}>{documentDescription || '—'}</Typography>
      </Box>

      {/* Amount */}
      <Box className={classes.amountSection}>
        <Typography className={classes.amountLabel}>{t('charges:amount')}</Typography>
        <AmountField
          index={index}
          name="amount"
          onAmountChange={onChargeAmountChange}
        />
      </Box>

      {/* Actions bar */}
      <Box className={classes.actionsBar}>
        <button type="button" className={classes.toggleBtn} onClick={onToggleParties}>
          <FontAwesomeIcon icon={isPartiesOpen ? 'chevron-up' : 'chevron-down'} size="xs" />
          <Typography style={{ fontSize: '0.75rem', color: 'inherit' }}>
            {isPartiesOpen ? t('charges:hideDetails') : t('charges:showDetails')}
          </Typography>
        </button>

        <Box className={classes.actionButtons}>
          <Tooltip title={t('orders:editDocumentTooltip')}>
            <IconButton
              className={classes.iconButton}
              onClick={() => onEdit(charge, index)}
              size="small"
            >
              <FontAwesomeIcon icon="edit" size="sm" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('orders:deleteDocumentTooltip')}>
            <IconButton
              className={classes.iconButton}
              onClick={() => onDelete(index)}
              size="small"
            >
              <FontAwesomeIcon icon="trash-alt" size="sm" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Expandable details */}
      <Collapse in={isPartiesOpen} timeout="auto" unmountOnExit>
        <Box className={classes.detailsSection}>
          <Box className={classes.detailRow}>
            <Typography className={classes.detailLabel}>{t('charges:buyerAtClosing')}</Typography>
            <AmountField
              index={index}
              name="buyer_at"
              onAmountChange={onChargePaidByAmountChange}
            />
          </Box>

          <Box className={classes.detailRow}>
            <Typography className={classes.detailLabel}>{t('charges:buyerBeforeClosing')}</Typography>
            <AmountField
              index={index}
              name="buyer_before"
              onAmountChange={onChargePaidByAmountChange}
            />
          </Box>

          {isCharges1200 === false && (
            <>
              <Box className={classes.detailRow}>
                <Typography className={classes.detailLabel}>{t('charges:sellerAtClosing')}</Typography>
                <AmountField
                  index={index}
                  name="seller_at"
                  onAmountChange={onChargePaidByAmountChange}
                />
              </Box>

              <Box className={classes.detailRow}>
                <Typography className={classes.detailLabel}>{t('charges:sellerBeforeClosing')}</Typography>
                <AmountField
                  index={index}
                  name="seller_before"
                  onAmountChange={onChargePaidByAmountChange}
                />
              </Box>

              <Box className={classes.detailRow}>
                <Typography className={classes.detailLabel}>{t('charges:by-others')}</Typography>
                <Box className={classes.detailAmountWithAction}>
                  <AmountField
                    index={index}
                    name="by_others"
                    onAmountChange={onChargePaidByAmountChange}
                  />
                  <Box className={classes.payerAction}>
                    <ContainedButton
                      text={t('charges:changePayers')}
                      size="small"
                      onClick={() => onEditSplitAmount(charge, 'Payer')}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};
